import { useEffect, useRef, useState, ReactNode } from "react";

interface LazyMountProps {
  children: ReactNode;
  /** px of root margin — start mounting before it scrolls into view */
  rootMargin?: string;
  /** Min height of the placeholder so layout doesn't jump */
  minHeight?: number;
}

/**
 * Defers mounting children until the placeholder scrolls near the viewport.
 * Massively reduces initial JS work, queries and image loads on mobile.
 */
export const LazyMount = ({ children, rootMargin = "300px", minHeight = 400 }: LazyMountProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (show || !ref.current) return;
    if (typeof IntersectionObserver === "undefined") { setShow(true); return; }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShow(true);
          obs.disconnect();
        }
      },
      { rootMargin }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [show, rootMargin]);

  if (show) return <>{children}</>;
  return <div ref={ref} style={{ minHeight }} aria-hidden="true" />;
};

export default LazyMount;
